import React, { useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { useMasterData } from 'src/api/masterData'
import { useLoginUser } from 'src/api/user'
import { RequestReasonIcon } from 'src/assets/svgs'
import { SmallButton } from 'src/elements/Button'
import {
  _reasongreen,
  _reasonoverlay,
  trueblack,
  truewhite,
} from 'src/styles/colors'
import styled, { css } from 'styled-components'
import { ModalProps, asModal } from '../hoc/asModal'

export type ShowReasonModalMeta = {
  postInterested: (reason: string | undefined, detail: string) => unknown
  openPaidUseModal?: () => void
  openSalesDemoModal?: () => void
}

const RequestReasonModalContent: React.FC<ModalProps<ShowReasonModalMeta>> = ({
  close,
  postInterested,
  openPaidUseModal,
  openSalesDemoModal,
}) => {
  const { user } = useLoginUser()
  const { data } = useMasterData()
  const { requestReasonOptions } = data || { requestReasonOptions: [] }
  const reasons = [...requestReasonOptions]
  const displayReasons = reasons.filter((r) =>
    user?.plan.freeAndPartner
      ? r.displayType === 'free' || r.displayType === 'all'
      : r.displayType === 'paid' || r.displayType === 'all',
  )

  const [reason, setReason] = useState<string | undefined>()
  const [detail, setDetail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const updateReason = (reason: string) => {
    setReason(reason)
  }

  const submit = async () => {
    if (user?.plan.salesDemo) {
      openSalesDemoModal && openSalesDemoModal()
      return
    }
    if (!reason) {
      alert(['理由を選択してください'])
      return
    }

    // 重複送信を防ぐ
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      await postInterested(reason, detail)
      close()
    } catch (error) {
      console.error('つながり申請の送信に失敗しました:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Layout>
      <Content>
        <Head>
          <HeadTitle>
            <HeadTitleIcon>
              <RequestReasonIcon />
            </HeadTitleIcon>
            <HeadTitleText>理由を選択してください</HeadTitleText>
          </HeadTitle>
          <HeadDescription>
            理由つきで送ると相手に特別な形で表示され
            <br />
            通常のつながりに比べて反応率が上がりやすくなります！
          </HeadDescription>
        </Head>
        <ReasonWrapper>
          <ReasonLabel>つながり申請理由</ReasonLabel>
          <ReasonItems>
            {displayReasons.map((r, k) => (
              <ReasonItem key={k}>
                <input
                  type={'radio'}
                  name="reason"
                  value={r.value}
                  onChange={(e) => updateReason(e.target.value)}
                  defaultChecked={r.value === reason}
                />
                <span>{r.label}</span>
                <div />
              </ReasonItem>
            ))}
            {user?.plan.freeAndPartner && (
              <PaidOnlyWrapper>
                <UnselectableReasonItem>
                  <input disabled type={'radio'} />
                  <span>自社のサービスを提案したい</span>
                  <div />
                </UnselectableReasonItem>
                <PaidOnlyMessage onClick={openPaidUseModal}>
                  有料プラン限定
                </PaidOnlyMessage>
              </PaidOnlyWrapper>
            )}
          </ReasonItems>
          <DetailFormDiv>
            <ReasonLabel>詳細をご記入ください（任意）</ReasonLabel>
            <DetailTextAreaDiv>
              <TextareaAutosize
                value={detail}
                minRows={4}
                maxRows={4}
                onChange={({ target: { value } }) => setDetail(value)}
              />
            </DetailTextAreaDiv>
          </DetailFormDiv>
        </ReasonWrapper>
        <Actions>
          <Action onClick={close} cancel={true} disabled={isSubmitting}>
            キャンセル
          </Action>
          <Action onClick={() => submit()} disabled={isSubmitting}>
            {isSubmitting ? '送信中...' : '選択した内容で送信'}
          </Action>
        </Actions>
      </Content>
    </Layout>
  )
}

const modalOverlayStyle = css`
  backdrop-filter: blur(6px) brightness(0.5);
  background-color: ${_reasonoverlay}80;
`

export const RequestReasonModal = asModal<ShowReasonModalMeta>(
  RequestReasonModalContent,
  {
    overlayStyle: modalOverlayStyle,
    scrollable: true,
  },
)

const Layout = styled.div`
  width: 520px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 25px;
  box-shadow: 0 5px 15px ${trueblack}80;
`

const Content = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px;
`

const Head = styled.div``

const HeadTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 8px;
`

const HeadTitleIcon = styled.div`
  margin-right: 8px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const HeadTitleText = styled.p`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
`

const HeadDescription = styled.span`
  padding-top: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
`

const ReasonWrapper = styled.div`
  padding: 24px 0 48px;
`

const ReasonLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #888;
  margin-bottom: 8px;
`

const ReasonItems = styled.div`
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const ReasonItemCss = css`
  margin: 6px 0;
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  padding: 6px 16px;
  border-radius: 100px;
  cursor: pointer;
  position: relative;

  > input {
    display: none;
  }

  > span {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    color: ${_reasongreen};
  }

  > div {
    z-index: 0;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 100px;
    border: solid 1px #ccc;
  }
  input:checked + span {
    position: relative;
    z-index: 1;
    color: #fff;
  }
  input:checked + span + div {
    border: solid 1px #02b7b0;
    background: #02b7b0;
  }
`

const ReasonItem = styled.label`
  ${ReasonItemCss}
`

const UnselectableReasonItem = styled.label`
  ${ReasonItemCss}
  cursor: not-allowed;
  background-color: #d9d9d9;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const Action = styled(SmallButton)<{ cancel?: boolean; disabled?: boolean }>`
  ${({ cancel, disabled }) =>
    cancel
      ? css`
          color: ${_reasongreen};
          background: ${truewhite};
          ${disabled &&
          css`
            color: #ccc;
            cursor: not-allowed;
          `}
        `
      : css`
          color: ${truewhite};
          background: ${_reasongreen};
          ${disabled &&
          css`
            background: #ccc;
            cursor: not-allowed;
          `}
        `}

  margin-left: 16px;
`

const DetailFormDiv = styled.div`
  margin-top: 14px;
`

const DetailTextAreaDiv = styled.div`
  border: 1px solid #cdcdcd;
  border-radius: 5px;

  background-color: #ffffffc0;
  backdrop-filter: blur(50px) brightness(1.75);
  padding: 14px;
  text-align: left;

  textarea {
    width: 100%;
    resize: none;
    font-size: 14px;
    letter-spacing: 0.036em;
    line-height: 1.5;
    overflow-y: auto;
    cursor: auto;
  }
`
const PaidOnlyWrapper = styled.div`
  display: flex;
  align-items: center;
`

const PaidOnlyMessage = styled.a`
  font-weight: 700;
  font-size: 14px;
  padding-left: 8px;
  color: red;
`
